// Browser side of image preparation: decode a file, run the pure pixel steps, re-encode small.
import { cutOutBackground, cornerColor, dominantColor } from '../shared/imaging';

const WORK_MAX = 1000;   // process at this size at most: plenty for a 720 × 480 screen, and quick on a phone

async function decode(file: Blob, name = 'that file') {
  let bmp: ImageBitmap;
  try { bmp = await createImageBitmap(file); }
  catch { throw new Error(`Couldn't read ${name}. Use a JPEG, PNG or WebP image.`); }
  const sc = Math.min(1, WORK_MAX / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * sc); c.height = Math.round(bmp.height * sc);
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  return { c, g };
}

function encode(src: HTMLCanvasElement, box: { x: number; y: number; w: number; h: number }, maxW: number, maxH: number): Promise<Blob> {
  const sc = Math.min(maxW / box.w, maxH / box.h, 1.5);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(box.w * sc)); out.height = Math.max(1, Math.round(box.h * sc));
  const g = out.getContext('2d')!;
  g.imageSmoothingQuality = 'high';
  g.drawImage(src, box.x, box.y, box.w, box.h, 0, 0, out.width, out.height);
  // WebP keeps transparency in a small file; browsers that can't write WebP give PNG instead
  return new Promise((ok, fail) => out.toBlob(b => (b ? ok(b) : fail(new Error('Could not prepare the image'))), 'image/webp', 0.84));
}

/** A player photo: cut out the plain backdrop (and the club badge), crop to the player, 440 px tall. */
export async function preparePhoto(file: File, o: { cutout: boolean; clearBadge: boolean }): Promise<Blob> {
  const { c, g } = await decode(file, file.name);
  let box = { x: 0, y: 0, w: c.width, h: c.height };
  if (o.cutout) {
    const img = g.getImageData(0, 0, c.width, c.height);
    const found = cutOutBackground(img.data, c.width, c.height, { clearBadge: o.clearBadge });
    if (!found) throw new Error(`${file.name} looks completely white, so there's no one to cut out.`);
    g.putImageData(img, 0, 0);
    box = found;
  }
  return encode(c, box, 520, 440);
}

/** A crest: optionally lose its white surround, then fit 320 × 320. */
export async function prepareCrest(file: File, cutout: boolean): Promise<Blob> {
  const { c, g } = await decode(file, file.name);
  let box = { x: 0, y: 0, w: c.width, h: c.height };
  if (cutout) {
    const img = g.getImageData(0, 0, c.width, c.height);
    const found = cutOutBackground(img.data, c.width, c.height);
    if (found) { g.putImageData(img, 0, 0); box = found; }
  }
  return encode(c, box, 320, 320);
}

/** A sponsor logo, kept whole, 340 × 340 at most, with its own background colour for the screen. */
export async function prepareLogo(file: File): Promise<{ blob: Blob; bg: string }> {
  const { c, g } = await decode(file, file.name);
  const bg = cornerColor(g.getImageData(0, 0, Math.min(8, c.width), Math.min(8, c.height)).data, Math.min(8, c.width));
  return { blob: await encode(c, { x: 0, y: 0, w: c.width, h: c.height }, 340, 340), bg };
}

/** Upload a prepared image; returns the address the club's details should store. */
export async function uploadImage(venue: string, adminCode: string, blob: Blob): Promise<string> {
  const r = await fetch(`/api/venue/${encodeURIComponent(venue)}/media`, { method: 'POST', body: blob, headers: { 'content-type': blob.type || 'image/png', 'x-admin-code': encodeURIComponent(adminCode) } });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.error ?? `Upload failed (${r.status})`);
  return out.url as string;
}

/** An opponent's crest for the league list: prepared like the club crest, plus its main colour. */
export async function prepareLeagueCrest(file: File, cutout: boolean): Promise<{ blob: Blob; color: string }> {
  const { c, g } = await decode(file, file.name);
  let box = { x: 0, y: 0, w: c.width, h: c.height };
  const img = g.getImageData(0, 0, c.width, c.height);
  if (cutout) { const found = cutOutBackground(img.data, c.width, c.height); if (found) { g.putImageData(img, 0, 0); box = found; } }
  return { blob: await encode(c, box, 320, 320), color: dominantColor(img.data, c.width, c.height) };
}

/** How long a video file runs, read from its own header before uploading. Null if the browser can't tell. */
export function videoDuration(file: File): Promise<number | null> {
  return new Promise(ok => {
    let url = '';
    try { url = URL.createObjectURL(file); } catch { return ok(null); }      // knowing the length is nice to have, never required
    const v = document.createElement('video');
    const done = (ms: number | null) => { URL.revokeObjectURL(url); ok(ms); };
    const timer = setTimeout(() => done(null), 8000);
    v.preload = 'metadata'; v.muted = true;
    v.onloadedmetadata = () => { clearTimeout(timer); done(Number.isFinite(v.duration) && v.duration > 0 ? Math.round(v.duration * 1000) : null); };
    v.onerror = () => { clearTimeout(timer); done(null); };
    v.src = url;
  });
}

/** Upload a promo video (MP4 or WebM, up to 30 MB). */
export async function uploadVideo(venue: string, adminCode: string, file: File): Promise<{ url: string; bytes: number }> {
  const type = file.type || (/\.webm$/i.test(file.name) ? 'video/webm' : 'video/mp4');
  const r = await fetch(`/api/venue/${encodeURIComponent(venue)}/video`, { method: 'POST', body: file, headers: { 'content-type': type, 'x-admin-code': encodeURIComponent(adminCode) } });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.error ?? `Upload failed (${r.status})`);
  return out as { url: string; bytes: number };
}
